import React, { useState, useEffect } from "react";

// Material UI Core Components
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Icon,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

// Custom Components
import Resources from "../Resources";
import OutlineReview from "./OutlineReview";
import ContentReview from "./ContentReview";
import StructureReview from "./StructureReview";
import Deliverables from "../Deliverables";
import { displayModuleStages } from "../Data/moduleStages";
import {
  module_status_enabled_tab_map as statusTabMap,
  course_steps,
} from "./CourseConstants";
import { fetchCourseAPI, updateModuleDetails } from "views/APIs/APIUtility";
import { Auth } from "aws-amplify";
import HeadingWithDescription from "components/Heading/HeadingWithDescription";

// Styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { stepperStyles, QontoConnector } from "views/Styles/stepperStyles";
import appbarStyles from "views/Styles/appbarStyles";

// Contexts
import NotificationContext from "views/Components/Context.js";

const useStyles = makeStyles((theme) => ({
  ...styles,
  ...stepperStyles(theme),
  ...appbarStyles(theme),
}));

// Local loader options (stage-specific messages)
const localLoaderOptions = {
  "In Outline Generation Queue":
    "Parsing input files, instructions and generating module outline...",
  "In Content Generation Queue":
    "Developing slide content, speaker notes, and module overview...",
  "In Structure Generation Queue":
    "Organizing slides, notes, and overview into final structure...",
  "In Deliverables Generation Queue":
    "Preparing module deliverables and requested artifacts...",
};

const LoadingStepIcon = () => <CircularProgress size={16} />;

export default function ModulesBoard(props) {
  const classes = useStyles();
  const { showNotification } = React.useContext(NotificationContext);
  const displaySteps = getDisplaySteps();

  // Extract courseID and moduleID from URL params
  const { courseID, moduleID } = props.match.params;

  // Stepper state
  const [activeStep, setActiveStep] = React.useState(0);
  const [moduleCompletedTillStep, setModuleCompletedTillStep] = React.useState(
    0
  );

  // Status + tabs
  const [courseStatus, setCourseStatus] = useState("");
  const [moduleStatus, setModuleStatus] = useState("");
  const [activeTab, setActiveTab] = useState("Resources");

  // Loading wrapper
  const [loading, setLoading] = useState({ isLoading: false, message: "" });

  // Entities
  const [course, setCourse] = useState({});
  const [module, setModule] = useState({});

  // In-between messaging
  const [
    inBetweenStagesLoaderMessage,
    setInBetweenStagesLoaderMessage,
  ] = useState("");

  // Auth user
  const [user, setUser] = useState("");

  // Only suppress the **content** loader after user clicks back; Stepper stays in loading
  const [suppressContentOnly, setSuppressContentOnly] = useState(false);

  // BIG: expected time line
  const WAIT_MSG = React.useMemo(() => {
    if (!localLoaderOptions.hasOwnProperty(moduleStatus)) return "";
    const min = 20 + Math.floor(Math.random() * 5);
    const max = 25 + Math.floor(Math.random() * 6);
    return `Expected time: ${min}-${max} minutes. You can leave this tab, processing will continue in the background.`;
  }, [moduleStatus]);

  // Real in-between (based on status)
  const isInBetween = React.useMemo(
    () => localLoaderOptions.hasOwnProperty(moduleStatus),
    [moduleStatus]
  );

  // Stepper should always reflect real loading when in-between
  const stepperInBetween = isInBetween;

  // Content loader can be hidden after user navigates back
  const contentInBetween = React.useMemo(
    () => isInBetween && !suppressContentOnly,
    [isInBetween, suppressContentOnly]
  );

  // Reset content suppression whenever status changes
  useEffect(() => {
    setSuppressContentOnly(false);
  }, [moduleStatus]);

  // Map to current base step (0..5)
  const baseStepIndex = React.useMemo(
    () => mapStatusToStep(moduleStatus?.trim?.() ?? moduleStatus),
    [moduleStatus]
  );

  // Stepper labels with an inserted "Loading…" step after base step
  const stepsWithIntermediate = React.useMemo(() => {
    const base = getDisplaySteps();
    if (!stepperInBetween) return base;
    const insertAt = baseStepIndex + 1;
    return [...base.slice(0, insertAt), "Loading…", ...base.slice(insertAt)];
  }, [stepperInBetween, baseStepIndex]);

  // Stepper active step (point to inserted loading step when in-between)
  const computedActiveStep = React.useMemo(
    () => (stepperInBetween ? baseStepIndex + 1 : baseStepIndex),
    [stepperInBetween, baseStepIndex]
  );

  // Completed-till should not advance just because we’re in queue
  const completedTill = React.useMemo(() => baseStepIndex, [baseStepIndex]);

  // Keep small stage message up to date
  useEffect(() => {
    const base = localLoaderOptions[moduleStatus] || "Loading...";
    setInBetweenStagesLoaderMessage(base);
  }, [moduleStatus]);

  // Fetch current user
  useEffect(() => {
    async function fetchUser() {
      try {
        const u = await Auth.currentAuthenticatedUser();
        setUser(u);
      } catch (error) {
        console.error("Error in fetchUser:", error);
        showNotification("Error fetching user details", "danger");
      }
    }
    fetchUser();
  }, []);

  // WebSocket to watch long-running tasks
  useEffect(() => {
    if (
      user === "" ||
      moduleID === undefined ||
      !localLoaderOptions.hasOwnProperty(moduleStatus)
    ) {
      return;
    }
    const wsUrl = `${process.env.REACT_APP_FASTAPI_URL.replace(
      "http",
      "ws"
    )}/ws/tasks/${user.username}/${moduleID}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => console.log("Task WebSocket connected");
    ws.onmessage = (event) => {
      if (event.data === "Done") {
        setInBetweenStagesLoaderMessage("Done!");
        window.location.reload();
      } else {
        setInBetweenStagesLoaderMessage("Failed!");
        setModuleStatus("Failed");
        window.location.reload();
      }
    };
    ws.onclose = () => console.log("Task WebSocket disconnected");

    return () => ws.close();
  }, [user, moduleID, moduleStatus]);

  // Fetch course status/module status
  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        const data = await fetchCourseAPI(courseID);
        if (data.status) setCourseStatus(data.status);
        const mod = data.modules?.find((m) => m.module_id === moduleID);
        if (mod?.status) setModuleStatus(mod.status);
      } catch (error) {
        console.error("Error fetching course details:", error);
      }
    };
    fetchCourseDetails();
  }, [courseID, moduleID]);

  // Fetch course + module data
  useEffect(() => {
    setLoading({ isLoading: true, message: "Loading Module..." });
    fetch(process.env.REACT_APP_FASTAPI_URL + `/get_course/${courseID}`)
      .then((res) => res.json())
      .then((data) => {
        setCourse(data);
        for (let i = 0; i < data.modules.length; i++) {
          if (data.modules[i].module_id === moduleID) {
            setModule(data.modules[i]);
            setActiveTab(statusTabMap[data.modules[i].status]);
            break;
          }
        }
      })
      .catch(() => {
        showNotification("Error fetching course data", "danger");
      })
      .finally(() => {
        setLoading({ isLoading: false, message: "" });
      });
  }, [courseID]);

  // Update Stepper active/completed positions
  useEffect(() => {
    setActiveStep(computedActiveStep);
    setModuleCompletedTillStep(completedTill);
  }, [computedActiveStep, completedTill]);

  // Map status to step index
  function mapStatusToStep(status) {
    return statusTabMap[status];
  }

  function getDisplaySteps() {
    return displayModuleStages;
  }

  // Clicking a step
  const handleStepChange = (index) => {
    const canAccess = mapStatusToStep(moduleStatus) >= index;
    if (!canAccess) {
      const message =
        course_steps[index].lockedMessage || "This step is locked.";
      showNotification(message, "danger");
      return;
    }

    // If in loading and user clicks a previous/at step:
    // - hide ONLY the content loader
    // - switch the content tab
    // - keep the stepper's active bubble on "Loading…"
    if (isInBetween && index <= baseStepIndex) {
      setSuppressContentOnly(true);
      setActiveTab(index);
      setActiveStep(computedActiveStep); // keep stepper on loading bubble
      return;
    }

    // Normal navigation
    setActiveTab(index);
    if (!isInBetween) {
      setActiveStep(index);
    }
  };

  const handleModuleDetailsChange = async (newModuleDetails) => {
    try {
      const formData = new FormData();
      formData.append("course_id", courseID);
      formData.append("module_id", moduleID);
      formData.append("module_name", newModuleDetails.title);
      formData.append("module_description", newModuleDetails.shortDescription);
      await updateModuleDetails(formData);
      setModule((prevModule) => ({
        ...prevModule,
        module_name: newModuleDetails.title,
        module_description: newModuleDetails.shortDescription,
      }));
      showNotification("Module details updated successfully", "success");
    } catch (error) {
      showNotification(
        "An error occurred while updating course details.",
        "danger"
      );
    }
  };

  // Content for intermediate state (content area only)
  const IntermediateContent = () => (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      minHeight="50vh"
      flexDirection="column"
      p={2}
    >
      <CircularProgress className={classes.loaderClass} />
      {/* BIG: expected time */}
      <Typography variant="h6" style={{ marginTop: 16, textTransform: "none" }}>
        {WAIT_MSG || "Working on it…"}
      </Typography>
      {/* SMALL: specific stage message */}
      {inBetweenStagesLoaderMessage && (
        <Typography
          variant="body2"
          style={{ marginTop: 8, opacity: 0.8, textTransform: "none" }}
        >
          {inBetweenStagesLoaderMessage}
        </Typography>
      )}
    </Box>
  );

  // Main content renderer
  const renderContent = () => {
    if (contentInBetween) return <IntermediateContent />;

    const activeStatusEntry = Object.entries(statusTabMap).find(
      ([_, value]) => value === activeTab
    );

    const activeStatusData = activeStatusEntry
      ? { [activeStatusEntry[0]]: activeStatusEntry[1] }
      : null;

    switch (activeTab) {
      case 0:
        return (
          <Resources
            projectID={courseID}
            moduleID={moduleID}
            projectType="course_designer"
            activeStatusData={activeStatusData}
            moduleStatus={moduleStatus}
          />
        );
      case 1:
        return (
          <OutlineReview
            courseID={courseID}
            moduleID={moduleID}
            activeStatusData={activeStatusData}
            moduleStatus={moduleStatus}
          />
        );
      case 2:
        return (
          <ContentReview
            courseID={courseID}
            moduleID={moduleID}
            activeStatusData={activeStatusData}
            moduleStatus={moduleStatus}
          />
        );
      case 3:
        return (
          <StructureReview
            courseID={courseID}
            moduleID={moduleID}
            activeStatusData={activeStatusData}
            moduleStatus={moduleStatus}
          />
        );
      case 4:
      case 5:
        return <Deliverables courseID={courseID} moduleID={moduleID} />;
      default:
        return null;
    }
  };

  // Initial page load
  if (loading.isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress className={classes.loaderClass} />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {moduleStatus === "In Publishing Queue" && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginBottom: 6,
          }}
        >
          <Icon style={{ color: "green" }}>done</Icon>
          <Typography
            variant="body1"
            style={{ marginLeft: 3, textTransform: "none" }}
          >
            Module is marked for publishing. Please publish the course from
            course dashboard.
          </Typography>
        </div>
      )}

      <Divider style={{ marginBottom: 16 }} />
      <HeadingWithDescription
        title={module.module_name}
        shortDescription={module.module_description}
        identifier="module"
        dialogTitle="Edit Module Details"
        onChange={handleModuleDetailsChange}
      />

      <AppBar
        position="static"
        className={classes.header}
        style={{
          border: "2px solid #20677b",
          borderRadius: "6px",
          margin: 0,
          marginTop: -16,
          padding: 0,
          minHeight: "40px",
        }}
      >
        <Toolbar style={{ minHeight: "40px", padding: "4px 8px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            width="100%"
            alignItems="center"
          >
            {/* Stepper */}
            <Stepper
              activeStep={activeStep}
              connector={<QontoConnector />}
              size="small"
              className={classes.stepperRoot}
              style={{
                flexGrow: 1,
                margin: 0,
                padding: "4px 0",
              }}
            >
              {stepsWithIntermediate.map((label, index) => {
                const isIntermediateIndex =
                  stepperInBetween && index === baseStepIndex + 1;
                const isDisabled = stepperInBetween
                  ? index > baseStepIndex + 1
                  : index > moduleCompletedTillStep;

                // Map UI index back to real index when a loading step is inserted
                const realIndex =
                  stepperInBetween && index > baseStepIndex ? index - 1 : index;

                return (
                  <Step
                    key={`${label}-${index}`}
                    onClick={() => {
                      if (isIntermediateIndex) {
                        // Clicking the loading step brings back loader in content
                        setSuppressContentOnly(false);
                      } else if (!isDisabled) {
                        handleStepChange(realIndex);
                      }
                    }}
                    style={{ padding: "0 4px" }}
                  >
                    <StepLabel
                      StepIconComponent={
                        isIntermediateIndex ? LoadingStepIcon : undefined
                      }
                      StepIconProps={
                        !isDisabled && !isIntermediateIndex
                          ? {
                              classes: {
                                root: classes.stepIcon,
                                active: classes.activeStepIcon,
                                completed: classes.completedStepIcon,
                              },
                            }
                          : {}
                      }
                      sx={{
                        "& .MuiStepLabel-label": {
                          display: "none",
                        },
                        "&:hover .MuiStepLabel-label": {
                          display: "block",
                          position: "absolute",
                          backgroundColor: "#20677b",
                          color: "white",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.7rem",
                          whiteSpace: "nowrap",
                          marginTop: "8px",
                          zIndex: 1000,
                          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        },
                        "& .MuiStepLabel-iconContainer": {
                          paddingRight: 0,
                        },
                      }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                );
              })}
            </Stepper>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box>{renderContent()}</Box>
    </Box>
  );
}
